import {
  Card,
  Divider,
  Flex,
  Form,
  Switch,
  Tag,
  theme,
  Tooltip,
  Typography
} from 'antd'
import {
  AlertTriangle,
  Lock,
  ShieldPlus,
  Users as UsersIcon
} from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { resolveIconColors } from 'src/components'

import { useStyles } from './styles'

type AdditionalSettingDescriptor = {
  key: 'enforce_sso' | 'allow_self_service_join' | 'auto_provision_users'
  i18nKey: string
  iconColor: string
  icon: ReactNode
  extraField?: ReactNode
}

const { Text } = Typography

export const AdditionalSettingsCard = () => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { styles } = useStyles()

  const settings: AdditionalSettingDescriptor[] = [
    {
      key: 'enforce_sso',
      i18nKey: 'workspace.authentication.additionalSettings.enforceSso',
      iconColor: '#DC2626',
      icon: <Lock size={14} />
    },
    {
      key: 'allow_self_service_join',
      i18nKey: 'workspace.authentication.additionalSettings.selfServiceJoin',
      iconColor: '#0E9F6E',
      icon: <UsersIcon size={14} />
    },
    {
      key: 'auto_provision_users',
      i18nKey: 'workspace.authentication.additionalSettings.autoProvision',
      iconColor: '#2563EB',
      icon: <ShieldPlus size={14} />,
      extraField: (
        <Flex align="center" gap={6} style={{ marginTop: 6 }} wrap="wrap">
          <Tooltip title={t('workspace.authentication.providers.mockOnlyHint')}>
            <Tag className={styles.attentionTag}>
              <AlertTriangle size={10} style={{ marginRight: 4 }} />
              {t('workspace.authentication.providers.mockOnlyLabel')}
            </Tag>
          </Tooltip>
        </Flex>
      )
    }
  ]

  return (
    <Card
      size="small"
      className={styles.settingsCard}
      styles={{ body: { padding: 0 } }}
    >
      <Flex
        justify="space-between"
        align="center"
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--ant-color-border-secondary)'
        }}
      >
        <Flex vertical style={{ lineHeight: 1.2 }}>
          <Text strong style={{ fontSize: 14 }}>
            {t('workspace.authentication.additionalSettings.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('workspace.authentication.additionalSettings.description')}
          </Text>
        </Flex>
      </Flex>
      {settings.map((row, idx) => {
        const tile = resolveIconColors(token, row.iconColor)

        return (
          <Fragment key={row.key}>
            <Flex
              align="flex-start"
              justify="space-between"
              gap={14}
              className={styles.settingRow}
            >
              <Flex
                align="flex-start"
                gap={11}
                style={{ minWidth: 0, flex: 1 }}
              >
                <span
                  className={styles.settingIcon}
                  style={{
                    background: tile.backgroundColor,
                    color: tile.iconColor
                  }}
                >
                  {row.icon}
                </span>
                <Flex vertical gap={4} style={{ minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    {t(`${row.i18nKey}.title`)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`${row.i18nKey}.description`)}
                  </Text>
                  {row.extraField}
                </Flex>
              </Flex>
              <Form.Item name={row.key} valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </Flex>
            {idx < settings.length - 1 ? (
              <Divider style={{ margin: 0 }} />
            ) : null}
          </Fragment>
        )
      })}
    </Card>
  )
}
